/**
 * Google Maps (location) profile module: persist a location **id** only;
 * resolve name/address/coords at read/preview time from the live location entity.
 */

export type GoogleMapsLocationSource = {
  name?: string | null;
  address?: string | null;
  coordinates?: {
    latitude?: number | null;
    longitude?: number | null;
  } | null;
};

export type GoogleMapsResolvedDisplay = {
  address: string;
  latitude?: number;
  longitude?: number;
  markerTitle?: string;
};

function finiteOrUndefined(
  value: number | null | undefined,
): number | undefined {
  return value != null && Number.isFinite(value) ? value : undefined;
}

/** Derive map caption + pin from a workspace location (or null if unusable). */
export function resolveGoogleMapsDisplayFromLocation(
  location: GoogleMapsLocationSource | null | undefined,
): GoogleMapsResolvedDisplay | null {
  if (!location) return null;

  const address = (location.address ?? '').trim();
  const latitude = finiteOrUndefined(location.coordinates?.latitude);
  const longitude = finiteOrUndefined(location.coordinates?.longitude);
  const markerTitle = (location.name ?? '').trim() || undefined;

  if (!address && latitude == null && longitude == null && !markerTitle) {
    return null;
  }

  return {
    address,
    latitude,
    longitude,
    markerTitle,
  };
}

/**
 * Overlay live location fields onto a google_maps module config for display.
 * When `locationId` is set but the location is missing, clears snapshot fields
 * so stale copied address/coords are not shown.
 */
export function applyGoogleMapsLocationToConfig(
  config: Record<string, unknown>,
  location: GoogleMapsLocationSource | null | undefined,
): Record<string, unknown> {
  const resolved = resolveGoogleMapsDisplayFromLocation(location);
  if (!resolved) {
    return {
      ...config,
      address: '',
      latitude: undefined,
      longitude: undefined,
      markerTitle: undefined,
      markerDescription: undefined,
    };
  }

  return {
    ...config,
    address: resolved.address,
    latitude: resolved.latitude,
    longitude: resolved.longitude,
    markerTitle: resolved.markerTitle,
    markerDescription: undefined,
  };
}

/**
 * Persistable google_maps config when a location is linked: keep `locationId`
 * (+ presentation settings) and drop duplicated address/coords that drift.
 */
export function toPersistedGoogleMapsConfig(
  config: Record<string, unknown>,
): Record<string, unknown> {
  const rawId = config.locationId;
  const locationId =
    typeof rawId === 'string' && rawId.trim() ? rawId.trim() : undefined;

  if (!locationId) {
    return config;
  }

  return {
    ...config,
    locationId,
    address: '',
    latitude: undefined,
    longitude: undefined,
    markerTitle: undefined,
    markerDescription: undefined,
  };
}
